import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Folder,
  Code2,
  Zap,
  ChevronRight,
  Sparkles,
  PlusCircle,
  AlertCircle
} from 'lucide-react';
import { projectsAPI } from '../services/api';
import StatsCard from '../components/Dashboard/StatsCard';
import ProjectCard from '../components/Dashboard/ProjectCard';
import QuickActions from '../components/Dashboard/QuickActions';
import Loader from '../components/UI/Loader';

const Dashboard = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalVersions: 0,
    frameworksUsed: 0
  });
  const [error, setError] = useState(null);
  const [isHovering, setIsHovering] = useState(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [projectsVisible, setProjectsVisible] = useState(false);
  const headerRef = useRef(null);
  const statsRef = useRef(null);

  // Create style tag for animations
  const styleTag = document.createElement('style');
  styleTag.innerHTML = `
    @keyframes gradient {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    @keyframes sparkle {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.7; transform: scale(1.1); }
    }

    @keyframes ripple {
      to {
        transform: scale(4);
        opacity: 0;
      }
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }

    @keyframes pulseGlow {
      0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.1); }
      50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.2); }
    }

    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
      20%, 40%, 60%, 80% { transform: translateX(5px); }
    }

    @keyframes bounceSlow {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-10px); }
    }

    .animate-gradient {
      background-size: 200% 200%;
      animation: gradient 3s ease infinite;
    }

    .animate-sparkle {
      animation: sparkle 2s ease-in-out infinite;
    }

    .animate-slide-up {
      animation: slideUp 0.8s ease-out forwards;
    }

    .animate-fade-in {
      animation: fadeIn 0.8s ease-out forwards;
    }

    .animate-float {
      animation: float 3s ease-in-out infinite;
    }

    .animate-pulse-glow {
      animation: pulseGlow 2s ease-in-out infinite;
    }

    .animate-shake {
      animation: shake 0.5s ease-in-out;
    }

    .animate-bounce-slow {
      animation: bounceSlow 2s ease-in-out infinite;
    }

    .sparkle-effect {
      position: relative;
      overflow: hidden;
    }

    .sparkle-effect::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%);
      transform: scale(0);
      opacity: 0;
      animation: ripple 0.6s linear;
    }

    .glass-morphism {
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .hover-lift {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .hover-lift:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    }

    .text-gradient {
      background: linear-gradient(90deg, #3b82f6, #8b5cf6, #3b82f6);
      background-size: 200% 200%;
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
    }

    .ripple {
      position: absolute;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.7);
      transform: scale(0);
      animation: ripple 0.6s linear;
    }
  `;
  document.head.appendChild(styleTag);

  useEffect(() => {
    loadProjects();

    // Trigger animations after load
    const timer = setTimeout(() => {
      setStatsVisible(true);
      setProjectsVisible(true);
    }, 300);

    // Intersection Observer for stats animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && statsRef.current) {
            const statsElements = statsRef.current.querySelectorAll('.stat-number');
            statsElements.forEach((el, index) => {
              setTimeout(() => {
                el.classList.add('animate-slide-up');
              }, index * 200);
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      clearTimeout(timer);
      observer.disconnect();

      // SAFELY remove style tag
      try {
        if (styleTag && styleTag.parentNode === document.head) {
          document.head.removeChild(styleTag);
        }
      } catch (error) {
        console.warn('Error removing style tag:', error);
      }
};
  }, []);

  useEffect(() => {
    // Parallax effect on scroll
    const handleScroll = () => {
      if (headerRef.current) {
        const scrolled = window.pageYOffset;
        const rate = scrolled * 0.5;
        headerRef.current.style.transform = `translateY(${rate}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      const projectsData = await projectsAPI.getAll();

      if (!projectsData) {
        console.error('No projects data received');
        setProjects([]);
        return;
      }

      const projectsArray = Array.isArray(projectsData)
        ? projectsData
        : (projectsData.data || []);

      setProjects(projectsArray);

      const totalVersions = projectsArray.reduce((sum, project) => {
        return sum + (project.versions_count || 0);
      }, 0);

      const frameworksUsed = new Set(
        projectsArray
          .map(p => p.framework)
          .filter(framework => framework && typeof framework === 'string')
      ).size;

      // Animate stats counting up
      animateStats(projectsArray.length, totalVersions, frameworksUsed);

    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const animateStats = (totalProjects, totalVersions, frameworksUsed) => {
    let startTotalProjects = 0;
    let startTotalVersions = 0;
    let startFrameworksUsed = 0;

    const duration = 1500;
    const steps = 60;
    const incrementTotalProjects = totalProjects / steps;
    const incrementTotalVersions = totalVersions / steps;
    const incrementFrameworksUsed = frameworksUsed / steps;

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      startTotalProjects += incrementTotalProjects;
      startTotalVersions += incrementTotalVersions;
      startFrameworksUsed += incrementFrameworksUsed;

      setStats({
        totalProjects: Math.min(Math.floor(startTotalProjects), totalProjects),
        totalVersions: Math.min(Math.floor(startTotalVersions), totalVersions),
        frameworksUsed: Math.min(Math.floor(startFrameworksUsed), frameworksUsed)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setStats({
          totalProjects,
          totalVersions,
          frameworksUsed
        });
      }
    }, duration / steps);
  };

  const createRipple = (event, element) => {
  // Check if element exists and is still in the DOM
  if (!element || !document.body.contains(element)) {
    console.warn('Element not found in DOM for ripple effect');
    return;
  }

  const ripple = document.createElement('span');
  const rect = element.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height);
  const x = event.clientX - rect.left - size / 2;
  const y = event.clientY - rect.top - size / 2;

  ripple.className = 'ripple';
  ripple.style.cssText = `
    width: ${size}px;
    height: ${size}px;
    top: ${y}px;
    left: ${x}px;
    position: absolute;
    pointer-events: none;
    z-index: 1;
  `;

  // Ensure element has proper positioning
  if (window.getComputedStyle(element).position === 'static') {
    element.style.position = 'relative';
    element.style.overflow = 'hidden';
  }

  // Add ripple to element
  element.appendChild(ripple);

  // Remove ripple after animation - FIXED LINE
  setTimeout(() => {
    try {
      // Check if ripple is still a child of the element before removing
      if (ripple.parentNode && ripple.parentNode === element) {
        element.removeChild(ripple);
      }
    } catch (error) {
      console.warn('Error removing ripple:', error);
    } finally {
      // Clean up styles we added
      if (document.body.contains(element)) {
        element.style.position = '';
        element.style.overflow = '';
      }
    }
  }, 600);
};

  const handleOpenProject = (project, event) => {
    const button = event.currentTarget;
    createRipple(event, button);

    setTimeout(() => {
      navigate('/projects', { state: { selectedProject: project } });
    }, 600);
  };

  const handleGenerateProject = (event) => {
    if (event) {
      const button = event.currentTarget;
      button.classList.add('sparkle-effect');

      setTimeout(() => {
        button.classList.remove('sparkle-effect');
        navigate('/generate');
      }, 600);
    } else {
      navigate('/generate');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <Loader text="Loading your dashboard..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 pb-20 overflow-x-hidden">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-purple-300/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-300/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-blue-400/5 rounded-full animate-float" />
      </div>

      <div className="relative z-10">
        {/* Header with parallax */}
        <div
          ref={headerRef}
          className="pt-8 px-4 sm:px-6 lg:px-8 transition-transform duration-300"
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="relative">
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg blur opacity-30 animate-pulse"></div>
                    <h1 className="relative text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient animate-gradient">
                      Dashboard
                    </h1>
                  </div>
                  <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-500 animate-sparkle" />
                </div>
                <p className="text-gray-600 mt-3 text-base sm:text-lg font-light animate-slide-up">
                  Welcome to your <span className="font-semibold text-blue-600">AI Project Generator</span> workspace
                </p>
              </div>

              <button
                onClick={handleGenerateProject}
                className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 sm:px-6 py-3 rounded-xl font-medium hover:shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-blue-500/30 sm:self-start"
                onMouseEnter={() => setIsHovering('generate')}
                onMouseLeave={() => setIsHovering(null)}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative flex items-center justify-center gap-2">
                  <PlusCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="text-sm sm:text-base">New Project</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
            <div className="glass-morphism border border-red-200 text-red-700 px-6 py-4 rounded-xl animate-shake">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards with staggered animation */}
        <div ref={statsRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 transition-all duration-700 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="transition-all duration-500 delay-100 hover-lift">
              <StatsCard
                title="Total Projects"
                value={stats.totalProjects}
                icon={<Folder className="h-6 w-6 sm:h-7 sm:w-7" />}
                color="blue"
                trend={projects.length > 0 ? "+12%" : "0%"}
                animated={true}
                index={0}
              />
            </div>
            <div className="transition-all duration-500 delay-200 hover-lift">
              <StatsCard
                title="Total Versions"
                value={stats.totalVersions}
                icon={<Code2 className="h-6 w-6 sm:h-7 sm:w-7" />}
                color="green"
                trend={stats.totalVersions > 0 ? "+24%" : "0%"}
                animated={true}
                index={1}
              />
            </div>
            <div className="transition-all duration-500 delay-300 hover-lift">
              <StatsCard
                title="Frameworks Used"
                value={stats.frameworksUsed}
                icon={<Zap className="h-6 w-6 sm:h-7 sm:w-7" />}
                color="purple"
                trend="+8%"
                animated={true}
                index={2}
              />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
          <div className={`transition-all duration-700 delay-300 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <QuickActions
              onGenerateProject={handleGenerateProject}
              onViewProjects={() => navigate('/projects')}
              animated={true}
            />
          </div>
        </div>

        {/* Recent Projects */}
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 transition-all duration-700 delay-500 ${projectsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="glass-morphism rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 hover:shadow-2xl transition-shadow duration-500">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
              <div>
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                  <span className="text-gradient">
                    Recent Projects
                  </span>
                  {projects.length > 0 && (
                    <span className="text-xs sm:text-sm font-medium bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded-full animate-pulse">
                      {projects.length} Active
                    </span>
                  )}
                </h2>
                <p className="text-gray-500 mt-1 sm:mt-2 text-sm sm:text-base">Your latest AI-generated projects</p>
              </div>

              {projects.length > 0 && (
                <button
                  onClick={() => navigate('/projects')}
                  className="group flex items-center gap-2 text-blue-600 hover:text-blue-500 text-sm font-medium transition-all duration-300 hover:gap-3 self-start"
                  onMouseEnter={() => setIsHovering('viewAll')}
                  onMouseLeave={() => setIsHovering(null)}
                >
                  <span>View All</span>
                  <ChevronRight className={`h-4 w-4 transition-transform duration-300 ${isHovering === 'viewAll' ? 'translate-x-1' : ''}`} />
                </button>
              )}
            </div>

            {projects.length === 0 ? (
              <div className="text-center py-12 sm:py-16 animate-fade-in">
                <div className="relative inline-block mb-4 sm:mb-6">
                  <div className="absolute -inset-3 sm:-inset-4 bg-blue-100/50 rounded-full blur-xl"></div>
                  <Folder className="relative h-16 w-16 sm:h-20 sm:w-20 text-gray-400 mx-auto" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">No projects yet</h3>
                <p className="text-gray-500 mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base sm:text-lg">
                  Start your journey by creating an AI-powered project with cutting-edge technology
                </p>
                <button
                  onClick={handleGenerateProject}
                  className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-medium text-base sm:text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-blue-500/40"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center gap-2 sm:gap-3">
                    <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span>Generate First Project</span>
                  </div>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {projects.slice(0, 6).map((project, index) => (
                  <div
                    key={project.id}
                    className={`transition-all duration-500 hover-lift ${
                      projectsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <ProjectCard
                      project={project}
                      onOpen={(e) => handleOpenProject(project, e)}
                      dataProjectId={project.id}
                      animated={true}
                      index={index}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Floating action button for mobile */}
        <div className="fixed bottom-6 right-6 z-50 md:hidden">
          <button
            onClick={handleGenerateProject}
            className="relative bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-2xl hover:shadow-blue-500/40 transition-all duration-300 hover:scale-110 animate-bounce-slow"
          >
            <PlusCircle className="h-6 w-6" />
            <span className="absolute -top-2 -right-2 h-6 w-6 bg-yellow-500 rounded-full flex items-center justify-center text-xs font-bold">
              <Sparkles className="h-3 w-3" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;